import { axiosInstance } from '../config/axiosConfig';
import { API_ENDPOINTS } from '../endpoints/endpoints';
import { CreditPackage } from './adminService';

export interface InitiateCardPaymentResponse {
  paymentId: string;
  amount: number;
  credits: number;
  label: string;
  paymentUrl?: string | null;
  upayUrl?: string | null;
  formFields?: Record<string, string> | null;
}

export interface CreditPurchaseResponse {
  paymentId: string;
  paymentUrl: string | null;
  amount: number;
  credits: number;
}

export interface CashPaymentResponse {
  paymentId: string;
  paymentCode?: string;
  amount: number | string;
  credits: number;
  instructions: string;
}

export interface PendingCashPayment {
  id: string;
  amount: number;
  description: string;
  createdAt: string;
  type: string;
  paymentCode: string | null;
}

export interface PaymentStatusResponse {
  id: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED';
  amount: number;
  currency: string;
  uftOrderId?: string;
}

export const creditsService = {
  getBalance: async (): Promise<{ balance: number; hasUsedFreeListing: boolean; enableFreeListing: boolean; isFree: boolean }> => {
    const { data } = await axiosInstance.get('/credits/balance');
    return data;
  },

  getPackages: async (): Promise<CreditPackage[]> => {
    const { data } = await axiosInstance.get(API_ENDPOINTS.ADMIN.CREDIT_PACKAGES);
    return data.success ? data.data : data;
  },

  // Primary: uses /pay/createcardorder, returns { paymentId, paymentUrl, credits }
  purchaseWithCard: async (packageId: string): Promise<CreditPurchaseResponse> => {
    const { data } = await axiosInstance.post(API_ENDPOINTS.PAYMENTS.PURCHASE_CREDITS, {
      packageId,
    });
    return data;
  },

  // Fallback: returns form fields to POST directly to https://merchant.upay.al/Pay
  initiateCardPayment: async (packageId: string): Promise<InitiateCardPaymentResponse> => {
    const { data } = await axiosInstance.post(API_ENDPOINTS.PAYMENTS.INITIATE_CREDITS, {
      packageId,
    });
    return data;
  },

  checkPaymentStatus: async (paymentId: string): Promise<PaymentStatusResponse> => {
    const { data } = await axiosInstance.get(API_ENDPOINTS.PAYMENTS.CHECK_STATUS(paymentId));
    return data;
  },

  purchaseCreditsCash: async (packageId: string): Promise<CashPaymentResponse> => {
    const { data } = await axiosInstance.post(API_ENDPOINTS.PAYMENTS.PURCHASE_CREDITS_CASH, { packageId });
    return data;
  },

  getPendingCashPayments: async (): Promise<PendingCashPayment[]> => {
    const { data } = await axiosInstance.get(API_ENDPOINTS.PAYMENTS.PENDING_CASH_PAYMENTS);
    return data;
  },

  getMyPayments: async (): Promise<any[]> => {
    const { data } = await axiosInstance.get(API_ENDPOINTS.PAYMENTS.MY_PAYMENTS);
    return data;
  },
};
